import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore, useIsAuthenticated } from '@/stores/authStore';
import { clearAuthStorage } from '@/services/keycloakAuthService';

const LOGOUT_EVENT_KEY = 'domainboost_advertiser_logout_event';
const LOGIN_EVENT_KEY = 'domainboost_advertiser_login_event';

/**
 * Custom hook for synchronizing logout/login state across browser tabs
 *
 * When a user logs out in one tab, this hook ensures all other tabs
 * are also logged out by listening to localStorage events.
 *
 * @example
 * ```tsx
 * function App() {
 *   useLogoutSync();
 *   return <AppContent />;
 * }
 * ```
 */
export function useLogoutSync() {
  const logout = useAuthStore((state) => state.logout);
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useIsAuthenticated();

  // Handle storage events from other tabs
  const handleStorageEvent = useCallback(
    (event: StorageEvent) => {
      // Logout event from another tab
      if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
        // Clear auth storage and update Zustand state
        clearAuthStorage();
        logout();
        // Redirect to login page
        window.location.href = '/login';
      }

      // Login event from another tab - restore session
      if (event.key === LOGIN_EVENT_KEY && event.newValue) {
        // Try to restore session from storage
        try {
          const stored = localStorage.getItem('auth-storage');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.state?.user && parsed.state?.isAuthenticated) {
              login(parsed.state.user);
            }
          }
        } catch {
          // Ignore parse errors
        }
      }
    },
    [logout, login]
  );

  // Set up storage event listener
  useEffect(() => {
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [handleStorageEvent]);

  // Track auth state changes across renders. The ref must persist between
  // renders (via useRef) so the cleanup can compare the *previous* auth
  // state with the current one — a per-effect object would always hold the
  // same value as `isAuthenticated`, making the transition check dead code.
  const prevAuthRef = useRef(isAuthenticated);
  useEffect(() => {
    // If we were authenticated and now we're not (logged out), broadcast
    if (prevAuthRef.current && !isAuthenticated) {
      broadcastLogout();
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);
}

/**
 * Broadcast logout event to other tabs
 * Call this function when performing a logout action
 */
export function broadcastLogout() {
  try {
    // Set and immediately remove to trigger storage event in other tabs
    localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
    // Use setTimeout to ensure the event is dispatched before removal
    setTimeout(() => {
      localStorage.removeItem(LOGOUT_EVENT_KEY);
    }, 100);
  } catch {
    // Ignore localStorage errors (e.g., in private browsing)
  }
}

/**
 * Broadcast login event to other tabs
 * Call this function after a successful login
 */
export function broadcastLogin() {
  try {
    // Set and immediately remove to trigger storage event in other tabs
    localStorage.setItem(LOGIN_EVENT_KEY, Date.now().toString());
    setTimeout(() => {
      localStorage.removeItem(LOGIN_EVENT_KEY);
    }, 100);
  } catch {
    // Ignore localStorage errors
  }
}

export default useLogoutSync;
